import React, { useEffect, useState } from 'react';
import { Box, Container, Typography, Alert, CircularProgress, Button, Card, CardContent, CardActions, IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import LogoutIcon from '@mui/icons-material/Logout';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Report } from '../types/report';
import ReportCard from '../components/ReportCard.tsx';

const Dashboard = () => {
   const [reports, setReports] = useState<Report[]>([]);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState('');
   const navigate = useNavigate();

useEffect(() => {
  const fetchReports = async () => {
    try {
      const token = localStorage.getItem("jwt");
      const headers = {
        ...(token && { Authorization: `Bearer ${token}` }),
      };
      const [reportRes] = await Promise.all([
        axios.get("https://backend.schnee.report/content-manager/collection-types/api::report.report?page=1&pageSize=1000&sort=valid_from:DESC", { headers })
      ]);
      setReports(reportRes.data.results);
    } catch (error) {
      setError('Fehler beim Laden der Reports');
    } finally {
      setLoading(false);
    }
  };

  fetchReports();
}, []);


   const handleNewReport = () => navigate('/report/create');

   const handleLogout = () => {
      localStorage.removeItem('jwt');
      navigate('/');
   };

   return (
      <Box sx={{ 
         minHeight: '100vh', 
         background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
         color: 'white', 
         py: 4 
      }}>
         <Container maxWidth="lg">
            <Box sx={{ 
               display: 'flex', 
               justifyContent: 'space-between', 
               alignItems: 'center', 
               mb: 5,
               background: 'rgba(255, 255, 255, 0.1)',
               backdropFilter: 'blur(10px)',
               borderRadius: '16px',
               padding: '24px 32px',
               boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
            }}>
               <Typography 
                  variant="h5" 
                  sx={{ 
                     fontWeight: 700,
                     letterSpacing: '-0.5px',
                     textShadow: '0 2px 10px rgba(0, 0, 0, 0.2)'
                  }}
               >
                  Schneeberichte
               </Typography>
               <IconButton
                  onClick={handleLogout}
                  sx={{ 
                     color: 'white',
                     background: 'rgba(255, 255, 255, 0.15)',
                     '&:hover': {
                        background: 'rgba(255, 255, 255, 0.25)',
                        transform: 'scale(1.05)',
                     },
                     transition: 'all 0.2s ease'
                  }}
                  title="Logout"
               >
                  <LogoutIcon />
               </IconButton>
            </Box>
            {loading ? (
               <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
                  <CircularProgress sx={{ color: 'white' }} size={60} thickness={4} />
               </Box>
            ) : error ? (
               <Alert 
                  severity="error" 
                  sx={{ 
                     borderRadius: '12px',
                     fontSize: '16px',
                     boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)'
                  }}
               >
                  {error}
               </Alert>
            ) : (
               <Box sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, 
                  gap: 3 
               }}>
                  <Card 
                     onClick={handleNewReport} 
                     sx={{ 
                        background: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(10px)',
                        cursor: 'pointer',
                        borderRadius: '16px',
                        border: '2px dashed rgba(102, 126, 234, 0.4)',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                           transform: 'translateY(-8px)',
                           boxShadow: '0 16px 48px rgba(102, 126, 234, 0.3)',
                           borderColor: 'rgba(102, 126, 234, 0.8)',
                        }
                     }}
                  >
                     <CardContent sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center',
                        minHeight: '200px',
                        justifyContent: 'center'
                     }}>
                        <Box sx={{
                           width: 80,
                           height: 80,
                           borderRadius: '50%',
                           background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                           display: 'flex',
                           alignItems: 'center',
                           justifyContent: 'center',
                           mb: 3,
                           boxShadow: '0 4px 20px rgba(102, 126, 234, 0.4)'
                        }}>
                           <AddIcon sx={{ fontSize: 40, color: 'white' }} />
                        </Box>
                        <Typography 
                           variant="h6" 
                           sx={{ 
                              fontWeight: 600,
                              color: '#333',
                              textAlign: 'center'
                           }}
                        >
                           Neuer Schneereport
                        </Typography>
                     </CardContent>
                     <CardActions sx={{ justifyContent: 'center', pb: 3 }}>
                        <Button 
                           size="medium" 
                           sx={{
                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              color: 'white',
                              fontWeight: 600,
                              px: 4,
                              py: 1,
                              borderRadius: '8px',
                              '&:hover': {
                                 background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                              }
                           }}
                        >
                           Erstellen
                        </Button>
                     </CardActions>
                  </Card>
                  {reports.map(report => (
                     <ReportCard key={report.documentId} report={report} />
                  ))}
               </Box>
            )}
         </Container>
      </Box>
   );
};

export default Dashboard;